/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { Link } from "@i18n/next";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import type { BibleManifest, BibleTranslations } from "@packages/sdk";
import {
  ContentMenuIcon,
  IconButton,
  numericPercentages,
  spacing,
} from "@packages/ui";
import type { CSSProperties } from "react";

import { isCustomDesktop, isLargeDevice } from "../bibleStyle.constants";
import { TranslationSelector } from "./TranslationSelector";

const navBoxStyle: Interpolation = {
  columnGap: spacing.s,
  display: "flex",
  flexDirection: "row",
  flexWrap: "nowrap",
  placeContent: "center",
  placeItems: "center",
};
const scrollStyle: Interpolation = {
  maxHeight: "60vh",
  maxWidth: numericPercentages[100],
  [isCustomDesktop]: { maxWidth: "max(calc(7 * 64px + 2 * 48px), 33vw)" },
  [isLargeDevice]: { maxWidth: "max(calc(7 * 64px), 50vw)" },
};

export type BibleNavigationProps = {
  tid: number | null;
  manifest: BibleManifest | null;
  translations: BibleTranslations["translations"];
};

export type RtlOrderFn = (
  index: number,
  itemsPerRow: number,
) => CSSProperties["order"];

export const BibleNavigation = ({
  tid,
  manifest,
  translations,
}: BibleNavigationProps) => {
  const { track } = useAnalytics();

  if (tid === null || !manifest) return null;

  return (
    <div css={navBoxStyle}>
      <TranslationSelector translations={translations} css={scrollStyle} />
      <IconButton
        asChild
        icon={<ContentMenuIcon />}
        size="m"
        variant="outline"
        onClickCapture={() =>
          track({
            event: HallowAnalyticsEvent.OpenedMySaves,
            properties: {
              entry_method: "tap_icon",
            },
          })
        }
      >
        <Link href="/library/saves" />
      </IconButton>
    </div>
  );
};
